import { JSX } from "react";
import { BaseComponent } from "../../Core/BaseComponent/BaseComponent";
import { SerendieSymbol, SerendieSymbolMoreVertical } from "@serendie/symbols";
import "./Styles.scss";
import { IconButton } from "@serendie/ui";
import { Menu } from "../../Dialogs/Menu/Menu";
import { MenuItem } from "../../Dialogs/Menu/MenuItem";
import { ArrayHelper } from "@iconics/core";

export class TreeView extends BaseComponent<ITreeViewProps, ITreeViewState>
{
    constructor(props: ITreeViewProps)
    {
        super(props);
        this.state = {};
    }

    public async componentDidMount(): Promise<void>
    {
        await this.initialise();
    }

    public async componentDidUpdate(prevProps: ITreeViewProps, prevState: ITreeViewState): Promise<void>
    {
        if (this.props.refreshToken && prevProps.refreshToken != this.props.refreshToken)
        {
            await this.initialise();
        }
    }

    private async initialise(): Promise<void>
    {
        await this.setStateAsync({});
        const nodes = await this.props.onGetNodes();

        const nodesWithChildren = nodes.map(i => ({
            ...i,
            children: undefined,
            expanded: false,
            leaf: false,

        })) satisfies ITreeViewItemWithChildren[];

        await this.props.onNodesChange(nodesWithChildren);

        if (this.props.selectedNodeId)
        {
            await this.expandToSelectedNode();
        }
        if (this.props.expandedNodeIds)
        {
            await this.expandNodes();
        }
    }

    private async expandToSelectedNode(): Promise<void>
    {
        if (!this.props.selectedNodeId)
        {
            return;
        }
        let ancestorIds = await this.props.onGetAncestorNodes(this.props.selectedNodeId);
        ancestorIds = ancestorIds.filter(i => i != this.props.selectedNodeId);
        let children = this.props.nodes;

        for (const ancestorId of ancestorIds)
        {
            const nodeToExpand = children.find(i => i.id == ancestorId);
            if (!nodeToExpand)
            {
                return;
            }
            else if (!nodeToExpand.expanded)
            {
                await this.handleNodeExpandCollapse(nodeToExpand);
            }

            const freshNodeToExpand = ArrayHelper
                .flattenTree(this.props.nodes, i => i.id, i => i.children ?? [])
                .find(i => i.id == ancestorId);

            children = freshNodeToExpand?.children ?? [];
        }
    }

    private async expandNodes(): Promise<void>
    {
        if (!this.props.expandedNodeIds || this.props.expandedNodeIds.length === 0) return;
        const remainingIds = [...this.props.expandedNodeIds];

        while (remainingIds.length > 0)
        {
            let madeProgress = false;
            const flatNodes = ArrayHelper.flattenTree(this.props.nodes, n => n.id, n => n.children ?? []);

            for (let i = 0; i < remainingIds.length; i++)
            {
                const id = remainingIds[i];
                const node = flatNodes.find(n => n.id === id);

                if (!node)
                {
                    continue;
                }

                remainingIds.splice(i, 1);
                madeProgress = true;

                if (!node.expanded && !node.leaf)
                {
                    await this.handleNodeExpandCollapse(node);
                }
                break;
            }

            if (!madeProgress)
            {
                break;
            }
        }
    }

    private async handleNodeExpandCollapse(node: ITreeViewItemWithChildren): Promise<void>
    {
        if (node.leaf)
        {
            return;
        }
        else if (node.expanded)
        {
            await this.setNodeState(node.id, n => ({ ...n, expanded: false }));
        }
        else if (node.children)
        {
            await this.setNodeState(node.id, n => ({ ...n, expanded: true }));
        }
        else
        {
            let nodesWithChildren: ITreeViewItemWithChildren[];
            try
            {
                const nodes = await this.props.onGetNodes(node.id);

                nodesWithChildren = nodes.map(i => ({
                    ...i,
                    children: undefined,
                    expanded: false,
                    leaf: false,

                })) satisfies ITreeViewItemWithChildren[];
            }
            catch
            {
                nodesWithChildren = [];
            }

            await this.setNodeState(node.id, n => ({
                ...n,
                expanded: true,
                leaf: nodesWithChildren.length == 0,
                children: nodesWithChildren,
            }));
        }
    }

    private async setNodeState(id: string, updateNode: (node: ITreeViewItemWithChildren) => ITreeViewItemWithChildren): Promise<void>
    {
        const copyNodes = (nodes: ITreeViewItemWithChildren[]): ITreeViewItemWithChildren[] =>
        {
            return nodes.map(node =>
            {
                if (node.id === id)
                {
                    return updateNode({ ...node });
                }
                else if (node.children)
                {
                    return { ...node, children: copyNodes(node.children) };
                }
                else
                {
                    return { ...node };
                }
            });
        };

        await this.props.onNodesChange(copyNodes(this.props.nodes));
    }

    private handleMenuShow(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, node: ITreeViewItem): void
    {
        event.stopPropagation();
        this.setState({ selectedNode: node, menuAnchor: event.currentTarget });
    }

    private handleMenuHide(): void
    {
        this.setState({ selectedNode: undefined, menuAnchor: undefined });
    }

    private handleMenuItemClick(menuItem: ITreeViewMenuItem): void
    {
        if (!this.state.selectedNode)
        {
            return;
        }
        menuItem.onClick(this.state.selectedNode);
        this.setState({ selectedNode: undefined, menuAnchor: undefined });
    }

    public render(): JSX.Element
    {
        return (
            <div
                className='tree-view'
            >
                {this.renderNodes(this.props.nodes, 0)}
                {
                    this.props.menu &&
                    <Menu
                        anchor={this.state.menuAnchor ?? null}
                        open={!!this.state.menuAnchor}
                        onClose={() => this.handleMenuHide()}
                        className='tree-view__menu'
                    >
                        {
                            this.props.menu
                                .filter(i => !i.visible || (!!this.state.selectedNode && i.visible(this.state.selectedNode)))
                                .map(i =>
                                    <MenuItem
                                        key={i.id}
                                        onClick={() => this.handleMenuItemClick(i)}
                                        className='tree-view__menu-item'
                                    >
                                        {i.name}
                                    </MenuItem>
                                )
                        }
                    </Menu>
                }
            </div>
        );
    }

    private renderNodes(nodes: ITreeViewItemWithChildren[], level: number): JSX.Element
    {
        const paddingLeft = 32 * level;
        const isNodeSelectable = !!this.props.onSelectedNodeChange;

        return (
            <>
                {nodes.map(node =>
                {
                    const expandIcon = (node.expanded ? 'chevron-down' : 'chevron-right');
                    const isSelected = (this.props.selectedNodeId && node.id === this.props.selectedNodeId);

                    return (
                        <>
                            <div
                                className='tree-view__node'
                                style={{
                                    paddingLeft: `${paddingLeft}px`,
                                }}
                            >
                                <div
                                    className='tree-view__expand'
                                    onClick={() => this.handleNodeExpandCollapse(node)}
                                >
                                    {!node.leaf && <SerendieSymbol name={expandIcon} />}
                                </div>
                                <div
                                    className={`tree-view__name ${isNodeSelectable ? 'tree-view__name--selectable' : ''} ${isNodeSelectable && isSelected ? 'tree-view__name--active' : ''}`}
                                    onClick={() => (isNodeSelectable ? this.props.onSelectedNodeChange(node) : this.handleNodeExpandCollapse(node))}
                                >
                                    {node.name}
                                </div>
                                {
                                    this.props.menu &&
                                    <div
                                        className='tree-view__menu-toggle'
                                    >
                                        <IconButton
                                            size='small'
                                            shape="circle"
                                            styleType="ghost"
                                            icon={<SerendieSymbolMoreVertical />}
                                            onClick={e => this.handleMenuShow(e, node)}
                                            style={{ flexShrink: 0 }}
                                        />
                                    </div>
                                }
                            </div>
                            {node.expanded && node.children && this.renderNodes(node.children, level + 1)}
                        </>
                    );
                }
                )}
            </>
        );
    }
}

export interface ITreeViewProps
{
    refreshToken?: string;
    nodes: ITreeViewItemWithChildren[];
    menu?: ITreeViewMenuItem[];
    selectedNodeId?: string;
    expandedNodeIds?: string[];
    onSelectedNodeChange?: (node: ITreeViewItemWithChildren) => void;
    onNodesChange: (items: ITreeViewItemWithChildren[]) => Promise<void>;
    onGetNodes: (parentId?: string) => Promise<ITreeViewItem[]>;
    onGetAncestorNodes: (nodeId: string) => Promise<string[]>
}

export interface ITreeViewState
{
    selectedNode?: ITreeViewItem;
    menuAnchor?: EventTarget & HTMLButtonElement;
}

export interface ITreeViewItem
{
    id: string;
    parentId?: string;
    name: string;
}

export interface ITreeViewItemWithChildren extends ITreeViewItem
{
    children?: ITreeViewItemWithChildren[];
    expanded: boolean;
    leaf: boolean;
}

export interface ITreeViewMenuItem
{
    id: string;
    name: string;
    visible?: (node: ITreeViewItem) => boolean;
    onClick: (node: ITreeViewItem) => void;
}
